import React, { useState } from 'react';
import {
  InfoWrapper,
  InfoRowContainer,
  InfoGrid,
  GridCell,
  TitleRow,
  InfoTitle,
  InfoContent,
  InfoIcon,
  DomainContainer,
  DomainItem,
  DomainIcon,
  DomainText,
  SectionTitle,
  SectionText,
  SectionTitleWrapper,
  ImportanceSubtitle,
  DomainItemWrapper,
  LeftDomainContent,
  RightIconsContainer,
  SectionWithIcon,
  SectionIconWrapper,
  JoinGroupButton,
  MajorPrepaButton,
  MajorPrepaButtonContent,
  MajorPrepaTitle,
  MajorPrepaText,
} from './styles';

import salaireImg from '../../assets/salaire_icon.png';
import poursuiteImg from '../../assets/diplome_icon.png';
import debouchesImg from '../../assets/debouches_icon.png';
import useDeviceType from '../useDeviceType';

// Domain icons
import defaultIcon from '../../assets/dont_know_icon.png';
import questionsIcon from '../../assets/questions_icon.png';
import majorPrepaIcon from '../../assets/major_prepa.png';
import ressourcesLinkIcon from '../../assets/ressources_link.png';

import plusIcon from '../../assets/plus_icon.png';
import dureeImg from '../../assets/duree.png';
import prixImg from '../../assets/prix.png';
import statutImg from '../../assets/statut.png';
import { useNavigate, useLocation } from 'react-router-dom';


// Styles pour la mise en page desktop
const desktopStyles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  topRowContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 2fr',
    gap: '20px',
    width: '100%',
    alignItems: 'flex-start',
  },
  infoCard: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  domains: {
    height: '100%',
  },
  bottomRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
    width: '100%',
    alignItems: 'stretch',
  },
  equalHeight: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  cardStyle: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    margin: 0,
    padding: 0,
  }
};

// Props will now include formacodeData and hierarchyCodeToIconMap
const InfoFormation = ({ formation, headerHeight, formacodeData, hierarchyCodeToIconMap, trackInteraction }) => {
  const { isMobile } = useDeviceType();
  const navigate = useNavigate();
  const location = useLocation();
  const [isButtonPressed, setIsButtonPressed] = useState(false);
  const [isMajorPrepaButtonPressed, setIsMajorPrepaButtonPressed] = useState(false);
  const [joinGroupTouchStart, setJoinGroupTouchStart] = useState(null);
  const [majorPrepaTouchStart, setMajorPrepaTouchStart] = useState(null);
    // States for formacode data are removed, will use props

  // isDataLoaded can be inferred from props presence
  const isDataLoaded = !!(formacodeData && formacodeData.formacodes && hierarchyCodeToIconMap);

  // Fonction pour obtenir les détails d'un domaine à partir de son code hierarchy
  const getDomainDetails = (hierarchyCode) => {
    if (!hierarchyCode || !isDataLoaded) {
      return { name: hierarchyCode || 'Domaine inconnu', icon: defaultIcon };
    }

    // Chercher dans les formacodes par hierarchyCode
    const formacodeEntry = formacodeData.formacodes.find(fc => fc.hierarchyCode === hierarchyCode);
    
    if (formacodeEntry) {
      // Vérifier si on a une icône spécifique pour ce domaine (usually for 4-char codes)
      const icon = hierarchyCodeToIconMap.get(hierarchyCode) || 
                   (hierarchyCode.length > 4 ? hierarchyCodeToIconMap.get(hierarchyCode.substring(0,4)) : defaultIcon) || 
                   defaultIcon;
      return {
        name: formacodeEntry.descriptorRich || formacodeEntry.descriptor || 'Domaine inconnu',
        icon: icon
      };
    }

    // Si pas trouvé par hierarchyCode exact, chercher dans les domaines parents (pour les codes > 4 chars)
    if (hierarchyCode.length > 4) {
      const parentHierarchyCode = hierarchyCode.substring(0, 4);
      const parentIcon = hierarchyCodeToIconMap.get(parentHierarchyCode);
      
      // Chercher le nom complet dans les formacodes (même si l'entrée exacte n'a pas été trouvée plus haut, ce qui est étrange)
      // This part might be redundant if the first check is comprehensive
      const childEntry = formacodeData.formacodes.find(fc => fc.hierarchyCode === hierarchyCode);
      if (childEntry) {
        return {
          name: childEntry.descriptorRich || childEntry.descriptor || 'Domaine inconnu',
          icon: parentIcon || defaultIcon
        };
      }
      
      // Si pas trouvé, essayer d'utiliser le nom du parent
      const parentEntry = formacodeData.formacodes.find(fc => fc.hierarchyCode === parentHierarchyCode);
      if (parentEntry) {
        return {
          // Prefer a more specific name if available, otherwise use parent's
          name: `Sous-domaine de ${parentEntry.descriptorRich || parentEntry.descriptor || 'Domaine parent inconnu'}`,
          icon: parentIcon || defaultIcon
        };
      }
    }

    // Fallback
    return { name: hierarchyCode || 'Domaine inconnu', icon: defaultIcon };
  };
  const renderJoinGroupButton = () => {
    if (!formation || !formation.id) return null;

    const fromParams = new URLSearchParams(location.search);
    fromParams.set('tab', 'infos');
    const fromPath = `${location.pathname}?${fromParams.toString()}`;

    const handlePressStart = (e) => {
      if (e.touches) {
        setJoinGroupTouchStart(e.touches[0].clientY);
      }
      setIsButtonPressed(true);
    };

    const handlePressEnd = (e) => {
      setIsButtonPressed(false);

      let isClick = false;
      if (joinGroupTouchStart !== null) {
        const touchEndY = e.changedTouches[0].clientY;
        if (Math.abs(touchEndY - joinGroupTouchStart) < 10) {
          isClick = true;
        }
      } else {
        isClick = true;
      }
      
      setJoinGroupTouchStart(null);

      if (isClick) {
        setTimeout(
          () =>
            navigate(`/social/formation/${formation.id}/chat`, {
              state: { from: fromPath },
            }),
          150
        ); // Delay navigation
      }
    };

    const handlePressCancel = () => {
      setIsButtonPressed(false);
      setJoinGroupTouchStart(null);
    };

    return (
      <JoinGroupButton
        active={isButtonPressed}
        onMouseDown={handlePressStart}
        onMouseUp={handlePressEnd}
        onMouseLeave={handlePressCancel}
        onTouchStart={handlePressStart}
        onTouchEnd={handlePressEnd}
        onTouchCancel={handlePressCancel}
      >
        <img src={questionsIcon} alt="" />
        Poser des questions aux étudiants
      </JoinGroupButton>
    );
  };

  const renderMajorPrepaButton = () => {
    const handlePressStart = (e) => {
      if (e.touches) {
        setMajorPrepaTouchStart(e.touches[0].clientY);
      }
      setIsMajorPrepaButtonPressed(true);
    };

    const handlePressEnd = (e) => {
      setIsMajorPrepaButtonPressed(false);

      let isClick = false;
      if (majorPrepaTouchStart !== null) {
        const touchEndY = e.changedTouches[0].clientY;
        if (Math.abs(touchEndY - majorPrepaTouchStart) < 10) {
          isClick = true;
        }
      } else {
        isClick = true;
      }
      
      setMajorPrepaTouchStart(null);

      if (isClick) {
        setTimeout(() => {
          trackInteraction('major_prepa_click');
          window.open("https://www.instagram.com/major_prepa/", "_blank");
        }, 150);
      }
    };

    const handlePressCancel = () => {
      setIsMajorPrepaButtonPressed(false);
      setMajorPrepaTouchStart(null);
    };

    return (
      <MajorPrepaButton
        active={isMajorPrepaButtonPressed}
        onMouseDown={handlePressStart}
        onMouseUp={handlePressEnd}
        onMouseLeave={handlePressCancel}
        onTouchStart={handlePressStart}
        onTouchEnd={handlePressEnd}
        onTouchCancel={handlePressCancel}
      >
        <img src={majorPrepaIcon} alt="Major Prépa" />
        <MajorPrepaButtonContent>
          <MajorPrepaTitle>Major Prépa</MajorPrepaTitle>
          <MajorPrepaText>est le média de référence pour les prépas qui souhaitent intégrer une école de commerce.
          </MajorPrepaText>
        </MajorPrepaButtonContent>
        <img src={ressourcesLinkIcon} alt="Ressources" style={{ marginLeft: 'auto', width: '18px', height: '18px' }} />
      </MajorPrepaButton>
    );
  };

  // Fonction pour rendre les domaines
  const renderDomains = () => {
    // Combiner les domaines principaux et secondaires
    // These are expected to be hierarchy codes
    const principalDomains = formation.domaines_enseignés_principaux || [];
    const secondaryDomains = formation.domaines_enseignés_secondaires || [];
    
    const allDomains = [
      ...principalDomains.map(d => ({ domainCode: d, type: 'principal' })),
      ...secondaryDomains.map(d => ({ domainCode: d, type: 'secondaire' }))
    ];

    if (!isDataLoaded) {
        return (
            <DomainContainer style={isMobile ? {} : { height: '100%', margin: 0 }}>
                <SectionTitleWrapper>
                    <SectionTitle>Domaines enseignés</SectionTitle>
                </SectionTitleWrapper>
                <DomainText>Chargement des données des domaines...</DomainText>
            </DomainContainer>
        );
    }
    
    if (allDomains.length === 0) {
      return (
        <DomainContainer style={isMobile ? {} : { height: '100%', margin: 0 }}>
          <SectionTitleWrapper>
            <SectionTitle>Domaines enseignés</SectionTitle>
            <ImportanceSubtitle>Poids dans la formation</ImportanceSubtitle>
          </SectionTitleWrapper>
          <DomainItem>
            <DomainItemWrapper>
              <LeftDomainContent>
                <DomainIcon>
                  <img src={defaultIcon} alt="Aucun domaine" />
                </DomainIcon>
                <DomainText>Aucun domaine spécifié</DomainText>
              </LeftDomainContent>
            </DomainItemWrapper>
          </DomainItem>
        </DomainContainer>
      );
    }

    return (
      <DomainContainer style={isMobile ? {} : { height: '100%', margin: 0 }}>
        <SectionTitleWrapper>
          <SectionTitle>Domaines enseignés</SectionTitle>
          <ImportanceSubtitle>Poids dans la formation</ImportanceSubtitle>
        </SectionTitleWrapper>

        {allDomains.map(({ domainCode, type }, idx) => {
          const domainDetails = getDomainDetails(domainCode);
          
          return (
            <DomainItem key={idx}>
              <DomainItemWrapper>
                <LeftDomainContent>
                  <DomainIcon>
                    <img src={domainDetails.icon} alt={domainDetails.name} />
                  </DomainIcon>
                  <DomainText>{domainDetails.name}</DomainText>
                </LeftDomainContent>
                
                <RightIconsContainer>
                  {Array.from({ length: type === 'principal' ? 2 : 1 }).map((_, i) => (
                    <img
                      key={i}
                      src={plusIcon}
                      alt="Importance"
                      style={{ width: '16px', height: '16px' }}
                    />
                  ))}
                </RightIconsContainer>
              </DomainItemWrapper>
            </DomainItem>
          );
        })}
      </DomainContainer>
    );
  };

  // Fonction pour rendre la grille d'informations
  const renderInfoGrid = () => (
    <InfoRowContainer style={isMobile ? {} : { margin: 0 }}>
      <InfoGrid>
        {/* Top Left: Durée */}
        <GridCell>
          <TitleRow>
            <InfoIcon>
              <img src={dureeImg} alt="durée" />
            </InfoIcon>
            <InfoTitle>Durée</InfoTitle>
          </TitleRow>
          <InfoContent>{formation.duree}</InfoContent>
        </GridCell>
        
        {/* Top Right: Coût */}
        <GridCell>
          <TitleRow>
            <InfoIcon>
              <img src={prixImg} alt="coût" />
            </InfoIcon>
            <InfoTitle>Coût</InfoTitle>
          </TitleRow>
          <InfoContent>{formation.cout} €/an</InfoContent>
        </GridCell>
        
        {/* Bottom Left: Statut */}
        <GridCell>
          <TitleRow>
            <InfoIcon>
              <img src={statutImg} alt="statut" />
            </InfoIcon>
            <InfoTitle>Statut</InfoTitle>
          </TitleRow>
          <InfoContent>{formation.public_prive}</InfoContent>
        </GridCell>
        
        {/* Bottom Right: Salaire de sortie */}
        <GridCell>
          <TitleRow>
            <InfoIcon>
              <img src={salaireImg} alt="salaire" />
            </InfoIcon>
            <InfoTitle>Salaire de sortie</InfoTitle>
          </TitleRow>
          <InfoContent>{formation.salaire_sortie || "Non spécifié"}</InfoContent>
        </GridCell>
      </InfoGrid>
    </InfoRowContainer>
  );

  // Fonction pour rendre la poursuite d'études
  const renderPoursuiteDEtudes = () => (
    <DomainContainer style={isMobile ? {} : { height: '100%', display: 'flex', flexDirection: 'column', margin: 0 }}>
      <SectionWithIcon>
        <SectionIconWrapper>
          <img src={poursuiteImg} alt="poursuite d'études" />
        </SectionIconWrapper>
        <SectionTitle>Poursuite d'études</SectionTitle>
      </SectionWithIcon>
      <div style={isMobile ? {} : { flex: 1 }}>
        {Object.values(formation.poursuite_etudes || {}).map((value, idx) => (
          <SectionText key={idx}>{value}</SectionText>
        ))}
      </div>
    </DomainContainer>
  );

  // Fonction pour rendre les débouchés professionnels
  const renderDebouchesProfessionnels = () => (
    <DomainContainer style={isMobile ? {} : { height: '100%', display: 'flex', flexDirection: 'column', margin: 0 }}>
      <SectionWithIcon>
        <SectionIconWrapper>
          <img src={debouchesImg} alt="débouchés" />
        </SectionIconWrapper>
        <SectionTitle>Débouchés Professionnels</SectionTitle>
      </SectionWithIcon>
      <div style={isMobile ? {} : { flex: 1 }}>
        {Object.values(formation.débouchés_professionnels || {}).map((value, idx) => (
          <SectionText key={idx}>{value}</SectionText>
        ))}
      </div>
    </DomainContainer>
  );

  // Mobile layout (original)
  if (isMobile) {
    return (
      <InfoWrapper headerHeight={headerHeight}>
        {renderInfoGrid()}
        {renderJoinGroupButton()}
        {formation && formation.type === 'cpge' && formation.nom_formation && (formation.nom_formation.toLowerCase().includes('ec') || formation.nom_formation.toLowerCase().includes('lettres')) && renderMajorPrepaButton()}
        {renderDomains()}
        {renderPoursuiteDEtudes()}
        {renderDebouchesProfessionnels()}
      </InfoWrapper>
    );
  }

  // Desktop layout (new grid layout)
  return (
    <InfoWrapper style={isMobile ? {} : { padding: '15px' }} headerHeight={headerHeight}>
      <div style={desktopStyles.container}>
        {/* Top Row: InfoGrid on left, Domains on right */}
        <div style={desktopStyles.topRowContainer}>
          <div style={{ ...desktopStyles.cardStyle, gap: '1rem' }}>
            {renderInfoGrid()}
            {renderJoinGroupButton()}
            {formation && formation.type === 'cpge' && formation.nom_formation && (formation.nom_formation.toLowerCase().includes('ec') || formation.nom_formation.toLowerCase().includes('lettres')) && renderMajorPrepaButton()}
          </div>
          <div style={desktopStyles.cardStyle}>
            {renderDomains()}
          </div>
        </div>
        
        {/* Bottom Row: Poursuite d'études on left, Débouchés on right */}
        <div style={desktopStyles.bottomRow}>
          <div style={desktopStyles.cardStyle}>
            {renderPoursuiteDEtudes()}
          </div>
          <div style={desktopStyles.cardStyle}>
            {renderDebouchesProfessionnels()}
          </div>
        </div>
      </div>
    </InfoWrapper>
  );
};

export default InfoFormation;